import { useState, type ReactNode } from 'react';
import growhubLockup from '../../assets/growhub-logo.png';
import { Button, Notice, Surface, TextField, uiStyles } from '../../components';
import { useAuth } from '../../app/auth/AuthContext';

/**
 * Sign-in gate — spec §4a. Email/password plus magic link. No account means
 * no access to the app; the session is the sole key to the data (RLS enforces
 * it server-side).
 */
export function AuthScreen(): ReactNode {
  const { signIn, signUp, sendMagicLink, resetPassword } = useAuth();

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [notice, setNotice] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setError(undefined);
    setNotice(undefined);

    if (!email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setBusy(true);

    const failure =
      mode === 'signin'
        ? await signIn(email.trim(), password)
        : await signUp(email.trim(), password);

    setBusy(false);

    if (failure) {
      setError(failure.message);
      return;
    }

    // signUp can return without a session when email confirmation is on.
    if (mode === 'signup') {
      setNotice(
        'Account created. Check your email to confirm it (locally this lands in the Inbucket inbox), or sign in if already confirmed.',
      );
    }
  };

  const magicLink = async (): Promise<void> => {
    setError(undefined);
    setNotice(undefined);

    if (!email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }

    setBusy(true);
    const failure = await sendMagicLink(email.trim());
    setBusy(false);

    if (failure) {
      setError(failure.message);
      return;
    }

    setNotice(
      'Sent. Check your email for the link (locally it lands in the Inbucket inbox at http://127.0.0.1:54324).',
    );
  };

  const forgot = async (): Promise<void> => {
    setError(undefined);
    setNotice(undefined);

    if (!email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }

    setBusy(true);
    const failure = await resetPassword(email.trim());
    setBusy(false);

    if (failure) {
      setError(failure.message);
      return;
    }

    setNotice('Check your email to reset your password.');
  };

  return (
    <main className={uiStyles.authShell}>
      <div className={uiStyles.authLayout}>
        <section className={uiStyles.authIntro} aria-labelledby="auth-brand">
          <p className={uiStyles.authEyebrow}>
            Grow tracking + shared evidence
          </p>
          <h1 id="auth-brand" className={uiStyles.authBrand}>
            <img
              className={uiStyles.authLockup}
              src={growhubLockup}
              alt="GrowHub"
              width={640}
              height={394}
            />
          </h1>
          <p className={uiStyles.authLead}>
            Track the grow. Learn from every cycle.
          </p>
          <p className={uiStyles.authSummary}>
            Keep the whole lifecycle connected—from spaces and plant batches to
            measurements, harvests, and the snapshots you choose to share.
          </p>
          <ul className={uiStyles.authBenefits}>
            <li>Keep grow records organized around the space they belong to.</li>
            <li>Build a reliable history from observations and measurements.</li>
            <li>Share only what you choose; private grow records stay private.</li>
          </ul>
        </section>

        <section className={uiStyles.authCard} aria-labelledby="auth-heading">
          <div className={uiStyles.authCardHeader}>
            <p className={uiStyles.authEyebrow}>Private by default</p>
            <h2 id="auth-heading" className={uiStyles.authCardTitle}>
              {mode === 'signin' ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className={uiStyles.hint}>
              Your private records sync to your account. Social sharing stays
              opt-in.
            </p>
          </div>

          <Surface as="div" tone="raised">
            <div
              className={uiStyles.authMode}
              role="group"
              aria-label="Sign in or create account"
            >
              <Button
                variant={mode === 'signin' ? 'secondary' : 'ghost'}
                aria-pressed={mode === 'signin'}
                onClick={() => {
                  setMode('signin');
                  setError(undefined);
                }}
              >
                Sign in
              </Button>
              <Button
                variant={mode === 'signup' ? 'secondary' : 'ghost'}
                aria-pressed={mode === 'signup'}
                onClick={() => {
                  setMode('signup');
                  setError(undefined);
                }}
              >
                Create account
              </Button>
            </div>

            <div className={`${uiStyles.stack} ${uiStyles.authForm}`}>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />

              <TextField
                label="Password"
                type="password"
                autoComplete={
                  mode === 'signin' ? 'current-password' : 'new-password'
                }
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />

              {error ? (
                <Notice tone="danger" role="alert">
                  {error}
                </Notice>
              ) : null}

              {notice ? (
                <Notice tone="info" role="status">
                  {notice}
                </Notice>
              ) : null}

              <Button
                variant="primary"
                className={uiStyles.authPrimaryAction}
                disabled={busy}
                onClick={() => void submit()}
              >
                {busy
                  ? 'Working…'
                  : mode === 'signin'
                    ? 'Sign in'
                    : 'Create account'}
              </Button>

              {mode === 'signin' ? (
                <>
                  <div className={uiStyles.authDivider}>or</div>
                  <Button
                    className={uiStyles.authSecondaryAction}
                    disabled={busy}
                    onClick={() => void magicLink()}
                  >
                    Email me a magic link
                  </Button>
                  <Button
                    variant="ghost"
                    className={uiStyles.authTertiary}
                    disabled={busy}
                    onClick={() => void forgot()}
                  >
                    Forgot password?
                  </Button>
                </>
              ) : null}
            </div>
          </Surface>
        </section>
      </div>
    </main>
  );
}
